import { useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { avviaAccessoNativo } from "@/integrations/supabase/accessoNativo";
import {
  accediConFinestraSicura,
  annunciaRitorno,
  valeLaPenaProvare as finestraSicuraPossibile,
} from "@/lib/accessoWeb";
import { apriPerAccesso } from "@/lib/apriEsterno";

/**
 * Gli accessi con un account che l'installatore ha gia' in tasca.
 *
 * QUALI, E PERCHE' NON GLI STESSI SU TUTTI I TELEFONI
 * Chi ha un Android ha un account Google per forza, chi ha un iPhone ha un
 * Apple ID per forza: sono i due account che non serve creare. Su ciascun
 * sistema si mostra per primo quello di casa.
 *
 * SU iOS APPLE NON E' UNA CORTESIA, E' UN OBBLIGO. Linea guida 4.8 dell'App
 * Store: un'app che usa un accesso social di terzi - Google e' nominato
 * espressamente - deve offrire come opzione equivalente un servizio che limiti
 * i dati a nome ed email e permetta di tenere l'email privata. Nessuna delle
 * cinque esenzioni ci copre: quella che sembra la nostra parla di account
 * aziendali GIA' ESISTENTI, mentre i nostri nascono dentro l'app. Senza questo
 * bottone, su iOS, o si toglie Google o l'app viene respinta.
 *
 * IL FLUSSO E' QUELLO WEB, e non per pigrizia: quello nativo passa dall'App ID
 * `it.datacomtecnologie.ai`, che a fine agosto 2026 e' ancora incastrato sul
 * team personale. Il flusso web usa un Services ID, che e' un identificativo
 * separato e si crea sul team aziendale: e' l'unica strada aperta finche' Apple
 * non risponde, e funziona anche dentro l'app.
 *
 * ⚠️ LA CHIAVE DI APPLE SCADE OGNI SEI MESI. Se nessuno la rigenera, l'accesso
 * con Apple smette di funzionare da solo, senza che sia cambiato niente. Sta
 * nel protocollo di recuperabilita' e in calendario.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * PERCHE' I BOTTONI SE LI CONTA DA SOLO (28/08)
 *
 * Prima c'era una costante `GOOGLE_LOGIN_ENABLED = false` scritta a mano in due
 * pagine. Serviva a non mostrare bottoni che non funzionavano, ma aveva due
 * difetti: nessuno si ricordava di cambiarla, e nessuno ricordava perche'
 * fosse li'. Risultato: acceso il provider nel pannello Supabase, l'accesso
 * restava comunque invisibile fino a un rilascio nuovo.
 *
 * Adesso il componente chiede a Supabase quali accessi sono DAVVERO accesi, e
 * mostra solo quelli. Se non ce n'e' nessuno non disegna niente - nemmeno la
 * riga "oppure". Non c'e' piu' un interruttore da ricordarsi: si accende il
 * provider nel pannello e il bottone compare da solo, senza toccare il codice.
 */
type Provider = "google" | "apple";

const ETICHETTE: Record<Provider, string> = {
  google: "Continua con Google",
  apple: "Accedi con Apple",
};

/**
 * I marchi, disegnati e non scaricati.
 *
 * Sono SVG dentro il codice per due motivi. Il primo e' che un logo caricato da
 * fuori e' una richiesta di rete in piu' proprio nella schermata di accesso: se
 * tarda, il bottone resta li' con un buco al posto dell'icona. Il secondo e' che
 * l'app gira anche dentro il guscio nativo, dove le immagini remote sono un
 * altro pezzo che si puo' rompere.
 *
 * Apple e Google chiedono entrambe che i loro marchi non si ridisegnino: la
 * mela e' monocromatica e prende il colore del testo, la G resta a quattro
 * colori anche su fondo scuro. Non vanno ricolorate per farle "intonare".
 */
/**
 * PERCHE' SONO BIANCHI, E NON SCURI COME IL RESTO (Davide, 28/08: «li farei piu'
 * leggibili»).
 *
 * Misurato sulla pagina vera: il bottone in stile "outline" aveva il fondo
 * `rgb(8,13,22)`, cioe' **lo stesso identico fondo della pagina** — contrasto
 * 1,00:1. Il testo sopra si leggeva benissimo (17,78:1), ma il bottone non
 * esisteva come oggetto: restava solo un bordo quasi invisibile, e accanto
 * all'«Accedi» blu sembrava disattivato.
 *
 * Il bianco non e' una scelta estetica presa a caso: e' la variante che Apple e
 * Google prevedono entrambe per i fondi scuri. La mela diventa nera perche'
 * segue il colore del testo, la G resta a quattro colori. E resta chiaro che
 * sono un'altra cosa dall'azione principale, che e' e resta il bottone blu.
 */
function LogoApple() {
  return (
    <svg viewBox="0 0 384 512" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

function LogoGoogle() {
  return (
    <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

const LOGHI: Record<Provider, () => JSX.Element> = {
  apple: LogoApple,
  google: LogoGoogle,
};

/** L'account "di casa" per primo: su iPhone Apple, altrove Google. */
function ordineProvider(): Provider[] {
  return Capacitor.getPlatform() === "ios" ? ["apple", "google"] : ["google", "apple"];
}

/**
 * Quali accessi esterni Supabase ha accesi in questo momento.
 *
 * `/auth/v1/settings` e' pubblico e non dice niente di riservato: elenca solo
 * quali provider sono configurati. Se la chiamata fallisce si risponde "nessuno":
 * meglio una schermata con la sola email che un bottone che porta a un errore.
 */
async function provideriAccesi(): Promise<Provider[]> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const chiave = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (!url || !chiave) return [];
  try {
    const r = await fetch(`${url}/auth/v1/settings`, { headers: { apikey: chiave } });
    if (!r.ok) return [];
    const dati = (await r.json()) as { external?: Record<string, boolean> };
    const accesi = ordineProvider().filter((p) => dati.external?.[p] === true);
    // REGOLA 4.8, APPLICATA DAL CODICE. Su iOS un'app che offre Google senza
    // offrire Apple viene respinta. La chiave di Apple scade ogni sei mesi:
    // se nessuno la rigenera, il provider si spegne da solo e l'app in
    // revisione (o in aggiornamento) diventa rifiutabile senza che nessuno
    // abbia toccato una riga. Meglio nascondere Google e restare con la sola
    // email: si perde una comodita', non la pubblicazione.
    if (Capacitor.getPlatform() === "ios" && !accesi.includes("apple")) {
      return accesi.filter((p) => p !== "google");
    }
    return accesi;
  } catch {
    return [];
  }
}

export function AccessiSocial({ disabilitato = false }: { disabilitato?: boolean }) {
  const [inCorso, setInCorso] = useState<Provider | null>(null);

  const { data: disponibili = [] } = useQuery({
    queryKey: ["accessi-social"],
    queryFn: provideriAccesi,
    // La configurazione cambia una volta ogni mai: non ha senso richiederla a
    // ogni apertura della pagina di accesso.
    staleTime: 10 * 60 * 1000,
  });

  const entra = async (provider: Provider) => {
    setInCorso(provider);

    // DENTRO L'APP NATIVA la destinazione non puo' essere window.location.origin:
    // l'app vive su un indirizzo interno (capacitor://localhost) che Supabase
    // non accetta, e il ripiego era il sito web - il login riusciva in Chrome
    // e l'app restava fuori (Davide dal telefono, 29/08). La destinazione e'
    // lo schema dell'app: il telefono la riapre e RitornoAccessoNativo
    // finisce il giro. Dal 05/09/2026 il giro nativo e' PKCE (audit, Sol 2):
    // parte da un client apposta, che tiene il verificatore nella memoria
    // dell'app; vedi integrations/supabase/accessoNativo.ts. Sul web resta
    // tutto com'era.
    if (Capacitor.isNativePlatform()) {
      try {
        const url = await avviaAccessoNativo(provider);
        // SU iOS: la finestra di Apple, che condivide i cookie di Safari e
        // restituisce l'indirizzo di ritorno qui dentro. Davide dall'iPhone,
        // 09/09: «si apre un browser che non ha i miei login gia' fatti» - era
        // SFSafariViewController, isolato da iOS 11 in poi. Vedi
        // lib/accessoWeb.ts; il giro si chiude qui, senza appUrlOpen.
        let ripiegare = !finestraSicuraPossibile();
        if (!ripiegare) {
          const esito = await accediConFinestraSicura(url);
          // L'indirizzo va a RitornoAccessoNativo, che e' l'unico posto che
          // scambia il codice e decide dove atterrare: due strade diverse per
          // la stessa cosa divergono, e chi se ne accorge e' l'installatore.
          if (esito.stato === "ok") annunciaRitorno(esito.url);
          // Chiusa col dito: non e' un errore, e non si ripiega - riaprire un
          // altro browser a chi ha appena annullato e' peggio del silenzio.
          else if (esito.stato === "ripiego") {
            console.warn("accesso_finestra_ripiego", esito.perche);
            ripiegare = true;
          }
        }
        if (ripiegare) {
          // Android, e i gusci senza il plugin: browser di sistema e ritorno
          // per deep link, che li' funziona. NON `window.open`: dentro la vista
          // web di iOS non fa niente, in silenzio (Davide, 08/09: «non parte
          // proprio niente»).
          await apriPerAccesso(url);
        }
      } catch (e) {
        toast.error((e as Error)?.message ?? "Accesso non riuscito");
      }
      // Il bottone si riarma: se la persona torna indietro senza completare,
      // deve poter ripremere.
      setInCorso(null);
      return;
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        // Solo Google: chiedere ad Apple di rimostrare il selettore non e'
        // previsto, e passargli parametri che non conosce fa fallire il giro.
        ...(provider === "google" ? { queryParams: { prompt: "select_account" } } : {}),
      },
    });
    if (error) {
      setInCorso(null);
      toast.error(error.message);
    }
  };

  if (disponibili.length === 0) return null;

  return (
    <>
      <div className="relative my-2">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-card px-2 text-xs uppercase text-muted-foreground">oppure</span>
        </div>
      </div>

      <div className="space-y-2">
        {disponibili.map((p) => {
          const Logo = LOGHI[p];
          return (
            <Button
              key={p}
              type="button"
              variant="outline"
              className={cn(
                "relative w-full border-transparent bg-white font-medium",
                "text-[#1f1f1f] hover:bg-white/90 hover:text-[#1f1f1f]",
              )}
              onClick={() => entra(p)}
              disabled={disabilitato || inCorso !== null}
            >
              {inCorso === p ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  {/* Il logo NON e' dentro il testo: cosi' resta a sinistra e
                      l'etichetta al centro anche quando le due scritte hanno
                      lunghezze diverse. */}
                  <span className="absolute left-4 flex items-center">
                    <Logo />
                  </span>
                  {ETICHETTE[p]}
                </>
              )}
            </Button>
          );
        })}
      </div>
    </>
  );
}
